import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, ArrowLeft, RefreshCw, PlugZap } from "lucide-react";
import { lovable } from "@/integrations/lovable/index";

/**
 * Surfaces OAuth / auth broker errors in plain language.
 * Providers redirect here with ?error=...&error_description=...
 */
const KNOWN: Record<string, { title: string; steps: string[] }> = {
  PROJECT_NOT_FOUND: {
    title: "This app isn't registered with the sign-in service yet",
    steps: [
      "Publish the app once — that registers it with the sign-in service.",
      "Try Google or Apple again after publishing.",
      "Email and password sign-in works right now, either way.",
    ],
  },
  access_denied: {
    title: "You cancelled the sign-in",
    steps: [
      "Head back and choose your account again.",
      "Make sure you approve the permission screen.",
    ],
  },
  server_error: {
    title: "The sign-in service had a problem",
    steps: [
      "Wait a moment, then try again.",
      "If it keeps happening, sign in with email and password instead.",
    ],
  },
};

const FALLBACK_STEPS = [
  "Try signing in again — most of these clear on a second attempt.",
  "Use email and password if the social provider keeps failing.",
  "Still stuck? Share the message below with support.",
];

const AuthError = () => {
  const [params] = useSearchParams();
  const code = params.get("error") ?? params.get("error_code") ?? "";
  const description =
    params.get("error_description") ?? params.get("message") ?? "";
  const known = KNOWN[code];
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const reRegister = async () => {
    setChecking(true);
    setStatus(null);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });
      if (result.redirected) return;
      if (result.error) {
        const message = String(result.error?.message ?? result.error);
        setStatus(
          /PROJECT_NOT_FOUND/i.test(message)
            ? "Still not registered. Publishing the app is what recreates the registration — do that once, then check again."
            : `Sign-in service replied: ${message}`,
        );
        return;
      }
      setStatus("Registration is live — you're signed in.");
    } catch (e) {
      setStatus(e instanceof Error ? e.message : String(e));
    } finally {
      setChecking(false);
    }
  };


  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Sign-in problem | WeKIT Career Clarity 360</title>
        <meta
          name="description"
          content="Something interrupted your sign-in. Here's what happened and what to do next."
        />
      </Helmet>

      <Card className="w-full max-w-lg rounded-3xl">
        <CardHeader className="space-y-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden />
          </div>
          <CardTitle className="font-display text-2xl">
            {known?.title ?? "Something interrupted your sign-in"}
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          <p className="text-muted-foreground">
            You're not locked out. Here's what to try next.
          </p>

          <ol className="space-y-2 text-sm">
            {(known?.steps ?? FALLBACK_STEPS).map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                  {i + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>

          {(code || description) && (
            <div className="rounded-2xl border border-border/60 bg-muted/30 p-4">
              <p className="text-xs font-medium text-muted-foreground mb-1.5">
                Message from the sign-in service
              </p>
              <p className="font-mono text-xs break-words">
                {[code, description].filter(Boolean).join(" — ")}
              </p>
            </div>
          )}

          <div className="rounded-2xl border border-border/60 p-4 space-y-3">
            <p className="text-sm font-medium">Re-register with the sign-in service</p>
            <p className="text-xs text-muted-foreground">
              This asks the broker to look up your app again. If it's still missing,
              publish the app once — that's what recreates the registration.
            </p>
            <Button
              onClick={reRegister}
              disabled={checking}
              variant="secondary"
              className="min-h-12 w-full"
            >
              <PlugZap className="mr-2 h-4 w-4" aria-hidden />
              {checking ? "Checking…" : "Re-register and check"}
            </Button>
            {status && (
              <p className="text-xs text-muted-foreground" role="status">
                {status}
              </p>
            )}
          </div>


          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="min-h-12 flex-1">
              <Link to="/auth">
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden /> Try signing in again
              </Link>
            </Button>
            <Button asChild variant="outline" className="min-h-12 flex-1">
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden /> Back home
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthError;
